/** Settings → General. Tenant identity + retention sliders. */
import { useEffect, useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { format } from "date-fns";
import { MetaRow, ComingOnline } from "./shared";
import {
  useTenant,
  useUpdateTenant,
  useRetention,
  useUpdateRetention,
  type TenantDTO,
} from "@/hooks/security/useEdrAdmin";

export function GeneralSection() {
  const tenantQ = useTenant();
  const retentionQ = useRetention();

  if (tenantQ.isLoading || retentionQ.isLoading) {
    return <Panel loading skeleton="card">{null}</Panel>;
  }

  const tenant = tenantQ.data?.kind === "ok" ? tenantQ.data.data : null;
  const retention = retentionQ.data?.kind === "ok" ? retentionQ.data.data : null;

  return (
    <div className="space-y-4">
      {tenant ? <TenantCard tenant={tenant} /> : (
        <Panel title="Tenant"><ComingOnline /></Panel>
      )}
      {retention ? <RetentionCard retention={retention} /> : (
        <Panel title="Retention"><ComingOnline /></Panel>
      )}
    </div>
  );
}

function TenantCard({ tenant }: { tenant: TenantDTO }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tenant.name);
  const update = useUpdateTenant();

  useEffect(() => setName(tenant.name), [tenant.name]);

  const planLabel = tenant.plan.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Panel
      title="Tenant"
      actions={
        editing ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setName(tenant.name); }}>Cancel</Button>
            <Button size="sm" disabled={update.isPending || !name.trim()} onClick={async () => {
              await update.mutateAsync({ name: name.trim() });
              setEditing(false);
            }}>{update.isPending ? "Saving…" : "Save"}</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Edit name</Button>
        )
      }
    >
      <div className="rounded border border-border divide-y divide-border">
        <div className="flex items-center justify-between gap-4 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Name</span>
          {editing ? (
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-7 max-w-xs" />
          ) : (
            <span className="text-foreground font-medium">{tenant.name}</span>
          )}
        </div>
        <MetaRow k="ID" v={<code className="font-mono text-[11px]">{tenant.id}</code>} />
        <MetaRow k="Created" v={format(new Date(tenant.createdAt), "MMM d, yyyy")} />
        <MetaRow
          k="Plan"
          v={
            tenant.trialDaysLeft != null
              ? `${planLabel} (trial — ${tenant.trialDaysLeft} days remaining)`
              : planLabel
          }
        />
        <MetaRow k="Sensors" v={`${tenant.sensorCount} of ${tenant.sensorLimit} used`} />
      </div>
    </Panel>
  );
}

function RetentionCard({ retention }: { retention: { eventRetentionDays: number; alertRetentionDays: number; auditRetentionDays: number; snapshotRetentionDays: number } }) {
  const [local, setLocal] = useState(retention);
  const update = useUpdateRetention();
  const dirty = JSON.stringify(local) !== JSON.stringify(retention);

  useEffect(() => setLocal(retention), [retention]);

  const ROWS: Array<[keyof typeof local, string, number, number]> = [
    ["eventRetentionDays", "Events", 7, 90],
    ["alertRetentionDays", "Alerts", 30, 730],
    ["auditRetentionDays", "Audit log", 90, 2555],
    ["snapshotRetentionDays", "Persistence snapshots", 7, 90],
  ];

  return (
    <Panel
      title="Retention"
      subtitle="How long each data class is kept before automatic deletion"
      actions={
        <>
          <Button size="sm" variant="ghost" disabled={!dirty} onClick={() => setLocal(retention)}>Reset</Button>
          <Button size="sm" disabled={!dirty || update.isPending} onClick={() => update.mutate(local)}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {ROWS.map(([key, label, min, max]) => (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-foreground">{label}</Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {local[key]} day{local[key] === 1 ? "" : "s"}
              </span>
            </div>
            <Slider
              min={min}
              max={max}
              step={1}
              value={[local[key]]}
              onValueChange={([v]) => setLocal((p) => ({ ...p, [key]: v }))}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>{min}d</span>
              <span>{max}d</span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
